import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { RootState } from 'store';
import userApi from 'api/user';
import stageApi from 'api/getStarted';
import { GetStartedPage } from './GetStartedPage';
import landingState from 'constants/landingState';
import { limitDescription } from './config';
import { setDocLinks } from 'actions/demoStage';

const FETCH_LIMITS_TIMEOUT = 120 * 1000;

const GetStartedPageContainer: FC = () => {
  const dispatch = useDispatch();
  const fetchLimitsTimeout = useRef<NodeJS.Timeout>();
  const [isLoadingLimits, setLoadingLimits] = useState(false);
  const [isLoadingDocLinks, setLoadingDocLinks] = useState(false);
  const user = useSelector((state: RootState) => state.user);
  const interfaceLang =
    useSelector((state: RootState) => state.localization.interfaceLang) || 'en';
  const docLinks = useSelector((state: RootState) => state.demoStage.docLinks);
  const { salesEmail } = useSelector((state: RootState) => state.settings);
  const [limits, setLimits] = useState([
    { text: '', icon: '', current: 0, max: 0 },
    { text: '', icon: '', current: 0, max: 0 },
    { text: '', icon: '', current: 0, max: 0 },
    { text: '', icon: '', current: 0, max: 0 },
  ]);

  const currentLegalEntity = useMemo(() => {
    const { activeLegalEntities } = user;

    return (
      activeLegalEntities.find(
        ({ boarding_status }) => boarding_status === 'onboarding_fill'
      ) ||
      activeLegalEntities.find(
        ({ boarding_status }) => boarding_status === 'onboarding_review'
      ) ||
      activeLegalEntities.find(
        ({ boarding_status }) => boarding_status === 'onboarding_draft'
      )
    );
  }, [user]);

  useEffect(() => {
    fetchLimits();
    fetchDocLinks();

    return () => {
      clearTimeout(fetchLimitsTimeout.current);
    };
  }, []);

  const fetchLimits = async () => {
    setLoadingLimits(true);

    try {
      const limits = await userApi.getRateLimitsInfo();

      setLimits(
        Object.keys(limits).map((key) => ({
          text: limitDescription[key].text,
          icon: limitDescription[key].icon,
          current: limits[key].rate_current.limit,
          max: limits[key].rate_max.limit,
        }))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLimits(false);

      fetchLimitsTimeout.current = setTimeout(
        fetchLimits,
        FETCH_LIMITS_TIMEOUT
      );
    }
  };

  const fetchDocLinks = async () => {
    setLoadingDocLinks(true);

    try {
      const docLinks = await stageApi.getDocLinks();
      dispatch(setDocLinks(docLinks));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDocLinks(false);
    }
  };

  if (user.landingState !== landingState.demoStage) {
    return <Redirect to={'/'} />;
  }

  return (
    <GetStartedPage
      boardingStage={currentLegalEntity.boarding_status}
      email={currentLegalEntity.sales_email || salesEmail}
      currentLegalEntityId={currentLegalEntity.id}
      docLinks={docLinks?.[interfaceLang]?.links.documentation}
      limits={limits}
      isLoadingLimits={isLoadingLimits}
      isLoadingDocLinks={isLoadingDocLinks}
    />
  );
};

export default GetStartedPageContainer;
